// The table as data. It consumes the engine's broadcasts and requests and keeps the state a
// renderer needs, with no DOM and no decisions of its own: the server is authoritative, this only
// remembers what it was told.
//
// Keeping it separate from the rendering is what makes it testable without a browser, and the
// reconnection replay is exactly the case worth testing: the server resends the whole history, so
// applying the same messages twice from a clean state has to land on the same table.

import { Protocol } from '../generated/protocol.js';

export const Choice = Object.freeze({
  NONE: 'none',
  CARD: 'card',
  BID: 'bid',
  RANK: 'rank',
  SEED: 'seed',
  // La vecia: the choice is a position in the neighbour's hand, and every option looks the same
  // from here, which is the whole point of the game
  DRAW: 'draw',
});

// The games that ever put chips in front of the player: the Marz declarations, the two auctions of
// the chiamata and the blind draw of the vecia. Everything else is answered by tapping a card. The
// chips go on the felt, so the table itself no longer has to know in advance; the probe of the
// table does, to measure a question only where one can be asked
const AsksToChoose = new Set([
  'MARZGAME',
  'BRISCOLA_CHIAMATA',
  'BRISCOLA_CHIAMATA_PUNTI',
  'BRISCOLA_CHIAMATA_SETTE',
  'BRISCOLA_CHIAMATA_SETTE_PUNTI',
  'VECIA',
]);

const tagOf = (object) => object?.[Protocol.JSONTagID];
const readCard = (object) =>
  object
    ? { seed: object[Protocol.JSONSeedID], cardType: object[Protocol.JSONCardTypeID] }
    : null;
const sameCard = (a, b) => Boolean(a && b && a.seed === b.seed && a.cardType === b.cardType);

export class TableState {
  constructor({ players, myPosition, deckType, gameType, vocabulary }) {
    this.players = players;
    this.myPosition = myPosition;
    this.deckType = deckType;
    this.gameType = gameType;
    // Whether a hand is scored on how many tricks you took rather than on what was in them. It is
    // the one thing the trick messages of that game do not say, and everything about the way a
    // seat reads follows from it
    this.countsTricks = gameType === 'MARZGAME';
    this.asksToChoose = AsksToChoose.has(gameType);
    this.formatPoints = pointsFormatterFor(gameType);
    // { seedName(seed), cardTypeName(type), cardName(card), bidLabel(...), rankNames(...) }
    this.vocabulary = vocabulary;
    this.reset();
  }

  reset() {
    this.matchesNumber = 0;
    this.handSize = 0;
    this.currentMatch = 0;
    this.auctionMode = 'CARD';
    this.callableRanks = 10;
    this.calledCardsNumber = 1;
    this.teams = null;

    // Our own hand holds real cards; the others are placeholders the server sends face down, so
    // only their count is meaningful
    this.hands = this.players.map(() => []);
    this.briscolaCard = null;
    this.briscolaSeed = null;
    this.deckLeft = 0;

    this.trick = [];
    this.trickComplete = false;
    this.lastTrickWinner = -1;
    this.matchPoints = this.players.map(() => 0);
    // Kept apart from the points: a game can count one, the other, or both
    this.tricksWon = this.players.map(() => 0);
    this.totals = this.players.map(() => 0);

    this.turn = -1;
    this.description = '';
    this.choice = { kind: Choice.NONE, options: [], validCards: null };

    this.callerPosition = -1;
    this.calledCards = [];
    this.requiredPoints = 0;
    this.partnerPositions = [];
    this.iAmThePartner = false;
    this.deletedPositions = [];

    this.results = null;
    this.finished = false;
    // La vecia: who has already run out of cards (safe) and who was left with the odd Jack
    this.outPositions = [];
    this.loserPosition = -1;
    // Marz game only: what each seat declared this hand, null until they say it, and how many
    // cards each was dealt - which is how many tricks there are to declare
    this.bets = this.players.map(() => null);
    this.dealtPerHand = 0;
    // Tressette only: the accusi each seat announced this hand, in the words a table uses
    this.declarations = this.players.map(() => []);
  }

  get myHand() {
    return this.hands[this.myPosition] ?? [];
  }

  nameOf(position) {
    return this.players[position] ?? 'qualcuno';
  }

  handCount(position) {
    return this.hands[position]?.length ?? 0;
  }

  // MarzGameFragment.maybeShowDeclarationsTotal: the tricks declared so far, or null before anybody
  // has said a number. Set against the tricks there are, it says whether the table is over or
  // under, and it is the sum the last to declare is not allowed to make come out even
  declaredTotal() {
    const said = this.bets.filter((bet) => bet !== null && bet !== undefined);
    return said.length === 0 ? null : said.reduce((sum, bet) => sum + bet, 0);
  }

  // A broadcast: something that happened, addressed to the whole table
  applyNotification(object) {
    switch (tagOf(object)) {
      case Protocol.JSONBriscolaSetupTag:
        return this.#setup(object);
      case Protocol.JSONStartMatchTag:
        return this.#startMatch(object);
      case Protocol.JSONRedealTag:
        this.description = 'Nessuno ha dichiarato: si ridà';
        return;
      case Protocol.JSONBidResponseTag:
        return this.#bidResponse(object);
      case Protocol.JSONCallTag:
        return this.#call(object);
      case Protocol.JSONPartnerRevealedTag:
        return this.#partnerRevealed(object);
      case Protocol.JSONPlayedValidCardTag:
        return this.#playedCard(object);
      case Protocol.JSONWinnerTag:
        return this.#trickWinner(object);
      case Protocol.JSONDrawnCardsTag:
        return this.#drawnCards(object);
      case Protocol.JSONPlayersPartialResultsTag:
        return this.#results(object, false);
      case Protocol.JSONPlayersFinalResultsTag:
        return this.#results(object, true);
      case Protocol.JSONDeletePlayerTag:
        return this.#deletedPlayer(object);
      case Protocol.JSONPassDoneTag:
        return this.#passDone(object);
      // Tressette: what everybody announced before the first card, which is worth points and says
      // something about what they are holding
      case Protocol.JSONDeclarationsTag:
        return this.#declarations(object);
      case Protocol.JSONHeartsBrokenTag:
        this.description = 'Cuori spezzati: da ora si può aprire a cuori';
        return;
      case Protocol.JSONPassCardsTag:
        return this.#someoneElsesTurn(object);
      // La vecia: a draw that happened, a player who ran out of cards, and the one left holding
      // the odd Jack at the end
      case Protocol.JSONCardDrawnTag:
        return this.#cardDrawn(object);
      case Protocol.JSONRemovedPlayerTag:
        return this.#playerIsSafe(object);
      case Protocol.JSONLoserTag:
        return this.#loser(object);
      // The Marz game: how many hands the game lasts, then one bet per player per hand
      case Protocol.JSONBetsNumberTag:
        this.matchesNumber = object[Protocol.JSONBetsNumberID] ?? this.matchesNumber;
        return;
      case Protocol.JSONBetResponseTag:
        return this.#betResponse(object);
      // Online only: the addressee gets the full request, everyone else this stripped copy, just
      // to see whose turn it is
      case Protocol.JSONValidCardsTag:
      case Protocol.JSONBidTag:
      case Protocol.JSONBetTag:
      case Protocol.JSONCallRankTag:
      case Protocol.JSONCallSeedTag:
      case Protocol.JSONCardChosenTag:
        return this.#someoneElsesTurn(object);
      default:
        return undefined;
    }
  }

  // A request addressed to a seat. forMe decides whether it becomes a prompt or just a turn marker
  applyRequest(object, position, forMe) {
    this.turn = position;
    if (!forMe) {
      return this.#someoneElsesTurn(object, position);
    }
    switch (tagOf(object)) {
      case Protocol.JSONValidCardsTag: {
        const validCards = (object[Protocol.JSONValidCardsID] ?? []).map(readCard);
        this.choice = { kind: Choice.CARD, options: [], validCards };
        this.description = 'Tocca a te: scegli la carta da giocare';
        return;
      }
      case Protocol.JSONPassCardsTag: {
        const options = (object[Protocol.JSONValidCardsID] ?? []).map(readCard);
        this.choice = { kind: Choice.CARD, options: [], validCards: options };
        const left = object[Protocol.JSONPassCardsLeftID] ?? 3;
        const to = object[Protocol.JSONPassToID] ?? -1;
        const direction = object[Protocol.JSONPassDirectionID] ?? Protocol.PASS_LEFT;
        const which = left >= 3 ? 'prima carta' : left === 2 ? 'seconda carta' : 'terza carta';
        this.description = `Passi ${passDirectionName(direction)} a ${this.nameOf(to)}: scegli la ${which} da dare`;
        return;
      }
      // The three pickers answer differently, which is easy to get wrong: a bid and a rank are
      // answered with the POSITION of the option, a seed with the option's own VALUE. Each option
      // therefore carries the number to send rather than letting the renderer guess
      case Protocol.JSONBetTag: {
        // Bets run from zero to the cards in hand, minus the one the last player may not say
        // because it would make the declarations add up to the number of tricks. The answer is
        // the bet itself, not its position in the list
        const forbidden = object[Protocol.JSONForbiddenBetID] ?? -1;
        const options = [];
        for (let bet = 0; bet <= this.myHand.length; bet += 1) {
          if (bet !== forbidden) options.push({ response: bet, label: String(bet) });
        }
        this.choice = { kind: Choice.BID, validCards: null, options };
        this.description = 'Quante prese fai?';
        return;
      }
      case Protocol.JSONBidTag: {
        const levels = object[Protocol.JSONBidOptionsID] ?? [];
        this.choice = {
          kind: Choice.BID,
          validCards: null,
          options: levels.map((level, index) => ({
            response: index,
            label: this.vocabulary.bidLabel(level, this.auctionMode, this.callableRanks),
          })),
        };
        this.description = 'Tocca a te: dichiara';
        return;
      }
      case Protocol.JSONCallRankTag: {
        const ranks = object[Protocol.JSONCallRankOptionsID] ?? [];
        const names = this.vocabulary.rankNames(this.callableRanks);
        this.choice = {
          kind: Choice.RANK,
          validCards: null,
          options: ranks.map((rank, index) => ({
            response: index,
            label: names[rank] ?? `Rango ${rank}`,
          })),
        };
        this.description = 'Scegli la carta da chiamare';
        return;
      }
      case Protocol.JSONCallSeedTag: {
        // Seeds travel as importance value minus one, and that same number is the answer
        const seeds = object[Protocol.JSONCallSeedOptionsID] ?? [];
        const index = object[Protocol.JSONCallSeedIndexID] ?? 0;
        const total = object[Protocol.JSONCalledCardsNumberID] ?? 1;
        this.choice = {
          kind: Choice.SEED,
          validCards: null,
          options: seeds.map((seed) => ({
            response: seed,
            label: this.vocabulary.seedName(seed + 1, this.deckType),
          })),
        };
        // Only the first seed named is the trump, and a table that calls two cards asks twice
        this.description =
          total <= 1
            ? 'Di che seme? Sarà la briscola'
            : `Carta ${index + 1} di ${total}: di che seme?${index === 0 ? ' Sarà la briscola' : ''}`;
        return;
      }
      case Protocol.JSONCardChosenTag: {
        // Every card of the neighbour's hand is face down, so the options are positions and
        // nothing else: what is being chosen is a place in their fan, not a card
        const handSize = object[Protocol.JSONNextPlayerHandSizeID] ?? 0;
        const from = object[Protocol.JSONNextPlayerID] ?? -1;
        const options = [];
        for (let index = 0; index < handSize; index += 1) {
          options.push({ response: index, label: `${index + 1}ª` });
        }
        this.choice = { kind: Choice.DRAW, validCards: null, options };
        this.description =
          from < 0
            ? 'Tocca a te: pesca una carta coperta'
            : `Tocca a te: pesca una carta coperta da ${this.nameOf(from)}`;
        return;
      }
      default:
        this.choice = { kind: Choice.NONE, options: [], validCards: null };
    }
  }

  // Index to answer with when the local player picks a card from their hand, or -1 if that card is
  // not playable. The engine answers by position in the valid cards, not in the hand
  responseForHandIndex(handIndex) {
    const card = this.myHand[handIndex];
    const valid = this.choice.validCards;
    if (!card || !valid) return -1;
    return valid.findIndex((candidate) => sameCard(candidate, card));
  }

  isPlayable(card) {
    return Boolean(this.choice.validCards?.some((candidate) => sameCard(candidate, card)));
  }

  clearChoice() {
    this.choice = { kind: Choice.NONE, options: [], validCards: null };
  }

  #someoneElsesTurn(object, positionHint) {
    const position = positionHint ?? object[Protocol.JSONPlayerPositionID] ?? -1;
    this.turn = position;
    const who = this.nameOf(position);
    switch (tagOf(object)) {
      case Protocol.JSONBidTag:
        this.description = `${who} sta dichiarando...`;
        break;
      case Protocol.JSONBetTag:
        this.description = `${who} sta scommettendo...`;
        break;
      case Protocol.JSONCallRankTag:
      case Protocol.JSONCallSeedTag:
        this.description = `${who} sta scegliendo la carta da chiamare...`;
        break;
      case Protocol.JSONCardChosenTag: {
        // Who is being drawn from is visible at a real table too: only the card is hidden
        const from = object[Protocol.JSONNextPlayerID] ?? -1;
        const target = from === this.myPosition ? 'te' : this.nameOf(from);
        this.description = from < 0 ? `${who} sta pescando...` : `${who} sta pescando da ${target}...`;
        break;
      }
      default:
        this.description = `Tocca a ${who}`;
    }
  }

  #setup(object) {
    this.matchesNumber = object[Protocol.JSONMatchesNumberID] ?? 0;
    this.handSize = object[Protocol.JSONHandSizeID] ?? 0;
    this.auctionMode = object[Protocol.JSONAuctionModeID] ?? 'CARD';
    this.callableRanks = object[Protocol.JSONCallableRanksID] ?? 10;
    this.calledCardsNumber = object[Protocol.JSONCalledCardsNumberID] ?? 1;
    this.teams = object[Protocol.JSONTeamsArrayID] ?? null;
  }

  #startMatch(object) {
    const hands = object[Protocol.JSONHandsID] ?? [];
    this.hands = hands.map((hand) => (hand ?? []).map(readCard));
    this.dealtPerHand = Math.max(0, ...this.hands.map((hand) => hand.length));
    this.deckLeft = object[Protocol.JSONDeckSizeID] ?? 0;

    const additional = object[Protocol.JSONAdditionalInformationID] ?? {};
    this.currentMatch = additional[Protocol.JSONCurrentMatchID] ?? this.currentMatch + 1;
    this.briscolaCard = additional[Protocol.JSONBriscolaID]
      ? readCard(additional[Protocol.JSONBriscolaID])
      : null;
    this.briscolaSeed = additional[Protocol.JSONBriscolaSeedID] ?? null;

    // A new deal wipes everything that belonged to the previous one
    this.trick = [];
    this.trickComplete = false;
    this.lastTrickWinner = -1;
    this.matchPoints = this.players.map(() => 0);
    this.tricksWon = this.players.map(() => 0);
    this.callerPosition = -1;
    this.calledCards = [];
    this.partnerPositions = [];
    this.iAmThePartner = false;
    this.turn = -1;
    this.results = null;
    this.bets = this.players.map(() => null);
    this.declarations = this.players.map(() => []);
    this.clearChoice();
    this.description = this.briscolaCard && this.briscolaSeed === null ? 'Si dichiara' : 'Si comincia';
  }

  // The accusi of Tressette, announced to the whole table before the first card is played. They
  // are worth points, and a client that drops them shows a pair's score moving with nothing to
  // explain it - which is what this one did until now
  #declarations(object) {
    const entries = object[Protocol.JSONDeclarationsID] ?? [];
    this.declarations = this.players.map(() => []);
    const said = [];
    for (const entry of entries) {
      const position = entry[Protocol.JSONPlayerPositionID] ?? -1;
      const label = this.#declarationLabel(entry);
      if (position < 0 || !label) continue;
      this.declarations[position].push(label);
      const thirds = entry[Protocol.JSONDeclarationThirdsID] ?? 0;
      said.push(`${this.nameOf(position)} ${label} (+${Math.trunc(thirds / 3)})`);
    }
    this.description = said.length > 0 ? said.join(', ') : 'Nessun accuso';
  }

  // TressetteGameFragment.shortDescribe: "tre assi", "napoletana a denari". The deck says "Asso",
  // and three of them are "tre assi": a label that reads the way the table talks is the point
  #declarationLabel(entry) {
    const kind = entry[Protocol.JSONDeclarationKindID] ?? Protocol.DECLARATION_SET;
    const value = entry[Protocol.JSONDeclarationValueID] ?? 0;
    const thirds = entry[Protocol.JSONDeclarationThirdsID] ?? 0;
    if (kind === Protocol.DECLARATION_NAPOLETANA) {
      const seed = this.vocabulary.seedName(value, this.deckType).toLowerCase();
      return seed ? `napoletana a ${seed}` : 'napoletana';
    }
    const how = Math.trunc(thirds / 3) === 4 ? 'quattro' : 'tre';
    // The Ace is the one rank whose plural is not its own name
    const name =
      value === 1 ? 'assi' : this.vocabulary.cardTypeName(value, this.deckType).toLowerCase();
    return name ? `${how} ${name}` : '';
  }

  #betResponse(object) {
    const position = object[Protocol.JSONPlayerPositionID];
    const bet = object[Protocol.JSONResponseID];
    this.bets[position] = bet;
    if (position === this.myPosition) {
      // Its own echo retires a bet prompt restored by a reconnection replay
      this.clearChoice();
      this.description = `Hai dichiarato ${bet}`;
    } else {
      this.description = `${this.nameOf(position)} dichiara ${bet}`;
    }
  }

  #bidResponse(object) {
    const position = object[Protocol.JSONPlayerPositionID];
    const level = object[Protocol.JSONBidLevelID];
    if (position === this.myPosition) {
      // Our own bid coming back: whatever prompt is on screen belongs to a request already answered
      this.clearChoice();
    }
    const label = this.vocabulary.bidLabel(level, this.auctionMode, this.callableRanks);
    this.description =
      position === this.myPosition ? `Hai dichiarato: ${label}` : `${this.nameOf(position)}: ${label}`;
  }

  #call(object) {
    this.callerPosition = object[Protocol.JSONCallerPositionID] ?? -1;
    if (this.callerPosition === this.myPosition) {
      // Same for the rank and seed pickers: the announced call is their echo
      this.clearChoice();
    }
    this.calledCards = (object[Protocol.JSONCalledCardsID] ?? []).map(readCard);
    this.briscolaSeed = object[Protocol.JSONBriscolaSeedID] ?? this.briscolaSeed;
    this.requiredPoints = object[Protocol.JSONRequiredPointsID] ?? 0;
    // The engine never says who the partner is: the only way to know before a called card falls is
    // to find it in your own hand
    this.iAmThePartner =
      this.callerPosition !== this.myPosition &&
      this.calledCards.some((called) => this.myHand.some((mine) => sameCard(mine, called)));
    this.partnerPositions = [];
    const names = this.calledCards.map((card) => this.vocabulary.cardName(card, this.deckType));
    const written =
      names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names.at(-1)}` : (names[0] ?? '');
    const caller =
      this.callerPosition === this.myPosition ? 'Chiami tu' : `${this.nameOf(this.callerPosition)} chiama`;
    this.description = `${caller} ${written}${this.iAmThePartner ? ' — sei tu il socio!' : ''}`;
  }

  #partnerRevealed(object) {
    const partner = object[Protocol.JSONPartnerPositionID] ?? -1;
    if (partner < 0 || this.partnerPositions.includes(partner)) return;
    this.partnerPositions.push(partner);
    const who = partner === this.myPosition ? 'Sei tu' : this.nameOf(partner);
    const of =
      this.callerPosition === this.myPosition ? 'chi chiama' : this.nameOf(this.callerPosition);
    this.description = `${who} il socio di ${of}`;
  }

  #playedCard(object) {
    const position = object[Protocol.JSONPlayerPositionID];
    const card = readCard(object[Protocol.JSONPlayedCardID]);

    // The previous trick stays on the table until the next card lands on it
    if (this.trickComplete) {
      this.trick = [];
      this.trickComplete = false;
    }
    this.trick.push({ position, card });
    this.#removeFromHand(position, card);
    if (position === this.myPosition) {
      // A replayed request restores a stale selection; its own echo is what retires it
      this.clearChoice();
    }
  }

  #removeFromHand(position, card) {
    const hand = this.hands[position];
    if (!hand) return;
    // Our own hand is real, so the exact card goes; the others are face down placeholders and only
    // the count matters
    const index = position === this.myPosition ? hand.findIndex((c) => sameCard(c, card)) : 0;
    if (index >= 0) hand.splice(index, 1);
  }

  // A trick is worth two different things depending on the game, and only one of them travels in
  // the message. The Briscola family sends the points the cards were worth; the Marz game sends
  // the winner and NOTHING else, because there the trick itself is the unit - you bet how many you
  // will take. Counting only what arrives left every declaration stuck at "0/3" all hand long,
  // which is exactly what MarzGameFragment avoids by keeping its own tricksByPosition
  #trickWinner(object) {
    const winner = object[Protocol.JSONWinnerID];
    const points = object[Protocol.JSONTrickPointsID] ?? 0;
    if (winner >= 0 && winner < this.matchPoints.length) {
      this.matchPoints[winner] += points;
      this.tricksWon[winner] += 1;
    }
    this.lastTrickWinner = winner;
    this.trickComplete = true;
    this.turn = -1;
    const who = winner === this.myPosition ? 'Prendi tu' : `${this.nameOf(winner)} prende`;
    // Where the trick is the unit, saying it was worth nothing would be saying the opposite of
    // what just happened
    this.description = this.countsTricks
      ? `${who} la presa`
      : `${who}${points > 0 ? ` (${this.formatPoints(points)})` : ' (niente)'}`;
  }

  #drawnCards(object) {
    for (const entry of object[Protocol.JSONDrawnCardsID] ?? []) {
      const position = entry[Protocol.JSONPlayerPositionID];
      const card = readCard(entry[Protocol.JSONAddedCardID]);
      if (this.hands[position]) this.hands[position].push(card);
    }
    this.deckLeft = object[Protocol.JSONDeckLeftID] ?? this.deckLeft;
  }

  #results(object, final) {
    const totals = object[Protocol.JSONPlayersResultsID] ?? [];
    const gains = totals.map((total, player) => total - (this.totals[player] ?? 0));
    this.totals = [...totals];
    const additional = object[Protocol.JSONAdditionalInformationID];
    const matchPoints = additional?.[Protocol.JSONMatchPointsArrayID] ?? null;
    this.results = {
      final,
      rows: totals.map((total, player) => ({
        position: player,
        name: this.nameOf(player),
        total,
        gain: gains[player],
        matchPoints: matchPoints?.[player] ?? null,
      })),
    };
    this.finished = final;
    this.turn = -1;
    this.clearChoice();
    this.description = final ? 'Partita finita' : `Fine mano ${this.currentMatch}`;
  }

  // Every hand this table knows about is swapped, but a player is only told their own share of it:
  // the three cards they were handed, which is exactly what they would see at a table
  #passDone(object) {
    const passes = object[Protocol.JSONPassesID] ?? [];
    if (passes.length === 0) {
      this.description = 'Questa mano non si passa niente';
      return;
    }
    const received = [];
    let from = -1;
    for (const entry of passes) {
      const giver = entry[Protocol.JSONPassFromID] ?? -1;
      const taker = entry[Protocol.JSONPassToID] ?? -1;
      const cards = (entry[Protocol.JSONPassedCardsID] ?? []).map(readCard);
      for (const card of cards) {
        this.#removeFromHand(giver, card);
        if (this.hands[taker]) this.hands[taker].push(card);
      }
      if (taker === this.myPosition) {
        received.push(...cards);
        from = giver;
      }
    }
    if (received.length === 0) {
      this.description = 'Le carte sono passate';
      return;
    }
    const names = received.map((card) => this.vocabulary.cardName(card, this.deckType));
    this.description = `Da ${this.nameOf(from)} ricevi: ${names.join(', ')}`;
  }

  // One draw: a card left the hand it was taken from, and either paired away with one the drawer
  // already had (both leave the game) or stayed with them. Only the two players party to the draw
  // are told which card it was; for everybody else it arrives face down, which is all their copy
  // of those hands holds anyway
  #cardDrawn(object) {
    const drawer = object[Protocol.JSONPlayerPositionID] ?? -1;
    const drawnFrom = object[Protocol.JSONDrawnFromPositionID] ?? -1;
    const paired = Boolean(object[Protocol.JSONPairMadeID]);
    const card = readCard(object[Protocol.JSONAddedCardID]);

    this.#removeFromHand(drawnFrom, card);
    const drawerHand = this.hands[drawer];
    if (drawerHand) {
      if (paired) {
        // The pair is the drawn card plus the one of the same rank the drawer already held
        const index =
          drawer === this.myPosition
            ? drawerHand.findIndex((mine) => mine?.cardType === card?.cardType)
            : 0;
        if (index >= 0) drawerHand.splice(index, 1);
      } else {
        drawerHand.push(drawer === this.myPosition ? card : { seed: 0, cardType: 0 });
      }
    }
    if (drawer === this.myPosition) {
      // Its own echo retires a draw prompt restored by a reconnection replay
      this.clearChoice();
    }
    this.turn = -1;
    const who = drawer === this.myPosition ? 'Peschi' : `${this.nameOf(drawer)} pesca`;
    const from = drawnFrom === this.myPosition ? 'da te' : `da ${this.nameOf(drawnFrom)}`;
    this.description = `${who} ${from}${paired ? ' e scarta la coppia' : ''}`;
  }

  #playerIsSafe(object) {
    const position = object[Protocol.JSONRemovedPlayerID] ?? -1;
    if (position < 0 || this.outPositions.includes(position)) return;
    this.outPositions.push(position);
    // "in salvo" and "resta" rather than "salvo"/"rimasto": a table's names carry no gender
    this.description =
      position === this.myPosition
        ? 'Sei in salvo: hai finito le carte'
        : `${this.nameOf(position)} è in salvo: ha finito le carte`;
  }

  #loser(object) {
    this.loserPosition = object[Protocol.JSONLoserID] ?? -1;
    this.turn = -1;
    this.description =
      this.loserPosition === this.myPosition
        ? 'Resti tu con la vecia in mano'
        : `Resta ${this.nameOf(this.loserPosition)} con la vecia in mano`;
  }

  #deletedPlayer(object) {
    const position = object[Protocol.JSONPlayerPositionID] ?? -1;
    if (position >= 0 && !this.deletedPositions.includes(position)) {
      this.deletedPositions.push(position);
      this.description = `${this.nameOf(position)} ha lasciato il tavolo`;
    }
  }
}

function passDirectionName(direction) {
  switch (direction) {
    case Protocol.PASS_LEFT:
      return 'a sinistra';
    case Protocol.PASS_RIGHT:
      return 'a destra';
    case Protocol.PASS_ACROSS:
      return 'di fronte';
    default:
      return '';
  }
}

// What a game's raw number actually is: how many of them make a point, and what those points are
// called. The engine hands out thirds for Tressette and penalties for Hearts, and a screen that
// prints the raw number says "34 pt" where the game says "11 e 1/3"
export function pointsScaleFor(gameType) {
  if (gameType === 'TRESSETTE') return { perPoint: 3, unit: 'pt' };
  if (gameType === 'HEARTS') return { perPoint: 1, unit: 'pen' };
  return { perPoint: 1, unit: 'pt' };
}

// Each game writes a SCORE its own way, exactly as the Android fragments override formatPoints:
// Tressette counts in thirds and says so, Hearts counts penalties. A score is always a whole
// number of thirds, which is why this can write the fraction out; an average is not, and
// pointsScaleFor is what that needs instead
export function pointsFormatterFor(gameType) {
  const { perPoint, unit } = pointsScaleFor(gameType);
  if (perPoint === 1) {
    return (points) => `${points} ${unit}`;
  }
  return (raw) => {
    const points = Math.trunc(raw / perPoint);
    const remainder = raw % perPoint;
    return remainder === 0 ? `${points} ${unit}` : `${points} e ${remainder}/${perPoint}`;
  };
}
